import { useNavigate, useParams } from "@tanstack/react-router";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { QuotationFormData } from "../../../shared/schemas/quotation.schema";
import { trpcClient } from "@/lib/trpc";
import { useQuotationStore } from "@/stores/useQuotationStore";
import QuotationForm from "./QuotationForm";
import { formatProductSpecs } from "@/lib/format-specs";
import { toast } from "sonner";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

import { toVnDateString } from "@apv-erp/status";
export default function QuotationEditPage() {
  const { quotationId } = useParams({ strict: false }) as {
    quotationId: string;
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateQuotation = useQuotationStore((s) => s.updateQuotation);

  const { data: quotationData, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["quotation", quotationId],
    queryFn: async () => {
      return trpcClient.quotations.getById.query({ id: quotationId });
    },
  });

  const quotation = quotationData as any;

  const handleSubmit = async (data: QuotationFormData) => {
    try {
      await trpcClient.quotations.update.mutate({
        id: quotationId,
        data: data as any,
      });

      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      await queryClient.invalidateQueries({
        queryKey: ["quotation", quotationId],
      });
      navigate({
        to: "/quotations/$quotationId",
        params: { quotationId },
      });
    } catch (err: any) {
      console.warn(
        "[QuotationEdit] tRPC failed, falling back to store:",
        err?.message,
      );
      const ok = await updateQuotation(quotationId, data as any);
      if (ok) {
        navigate({
          to: "/quotations/$quotationId",
          params: { quotationId },
        });
      } else {
        toast.error(err?.message || "Cập nhật thất bại");
      }
    }
  };

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !quotation) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Báo giá", to: "/quotations" }]}
        entityLabel="Báo giá"
        backTo="/quotations"
        codeOrId={quotationId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !quotation)
    return (
      <div className="w-full p-4">
        <Skeleton className="h-96 w-full" />
      </div>
    );

  const fmtDateInput = (v: any) =>
    v ? toVnDateString(v) : "";

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Báo giá", to: "/quotations" },
        { label: quotation.quotationId, to: `/quotations/${quotationId}` },
        { label: "Chỉnh sửa" },
      ]}
      title={`Sửa ${quotation.quotationId}`}
      status={
        <StatusBadge type="QuotationStatus" value={quotation.status} />
      }
      onBack={() => navigate({ to: `/quotations/${quotationId}` })}
      showContextPanel={false}
    >
      <div className="w-full space-y-4 animate-in fade-in duration-300">
        <QuotationForm
          defaultValues={{
            quotationId: quotation.quotationId,
            customerId: quotation.customerId ?? "",
            customerName: quotation.customerName ?? "",
            details:
              quotation.details?.map((d: any) => ({
                id: d.id ?? "",
                productId: d.productId || d.product?.id || "",
                productCode: d.productCode || d.product?.productCode || "",
                productName: d.productName || d.product?.productName || "",
                specs: typeof d.specs === "string" ? d.specs : formatProductSpecs(d.specs, d),
                process: d.process ?? "",
                unit: d.unit ?? "",
                quantity: d.quantity ?? 0,
                priceVND: Number(d.priceVND ?? 0),
                quotedPrice: d.quotedPrice ? Number(d.quotedPrice) : Number(d.priceVND ?? 0),
                priceUSD: Number(d.priceUSD ?? 0),
                costPrice: Number(d.costPrice ?? 0),
                profitMargin: Number(d.profitMargin ?? 0),
                notes: d.notes ?? "",
              })) || [],
            vatRate: quotation.vatRate ?? 10,
            currency: quotation.currency as any,
            exchangeRate: quotation.exchangeRate
              ? Number(quotation.exchangeRate)
              : undefined,
            deposit: quotation.deposit ?? "",
            shippingFee: Number(quotation.shippingFee ?? 0),
            margin: quotation.margin ? Number(quotation.margin) : undefined,
            email: quotation.email ?? "",
            creatorId: quotation.creatorId ?? "",
            progress: quotation.progress ?? "",
            createdDate: fmtDateInput(quotation.createdDate),
            validUntil: fmtDateInput(quotation.validUntil),
            deliveryAddress: quotation.deliveryAddress ?? "",
            contactPerson: quotation.contactPerson ?? "",
            phone: quotation.phone ?? "",
            paymentTerm: quotation.paymentTerm ?? "",
            deliveryTime: quotation.deliveryTime ?? "",
            status: quotation.status,
            notes: quotation.notes ?? "",
          }}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: `/quotations/${quotationId}` })}
          submitLabel={quotation.status === "DRAFT" ? "Lưu" : "Cập nhật"}
          orderCount={quotation._count?.orders || 0}
        />
      </div>
    </StandardDetailLayout>
  );
}
